/**
 * settings-fields — form controls for the Settings screens.
 *
 * `FieldSelect` is a THIN WRAPPER over the design system's `Dropdown`
 * (`@v0y4ge/jrni-ui`), not a parallel control. Corrected 2026-08-04: the first
 * version hand-rewrote the listbox (ARIA, keyboard, type-ahead) and, because it
 * positioned its menu with `position:absolute` inside the settings scroll pane,
 * clipped the options for any field low in the pane. `Dropdown` already ships the
 * combobox semantics AND a `portal` prop that renders the menu into
 * `document.body` — this wrapper turns that on, adds the label/help/required
 * presentation the settings forms use, and maps each option's hint onto the DS
 * option's `supportingText`. Nothing about the control is forked.
 *
 * `UrlField` is the URL input with real validation (regex + a URL parse), an
 * inline error, `aria-invalid`/`aria-describedby`, and a normalise-on-blur that
 * adds a missing scheme instead of scolding the user for omitting it.
 */
import { useEffect, useId, useMemo, useState } from "react";
import { Dropdown, Icon, T, type DropdownOption } from "@v0y4ge/jrni-ui";
import { useT } from "../../lib/i18n";

export interface FieldOption { value: string; label?: string; labelKey?: string; hint?: string }

const fieldLabel: React.CSSProperties = { fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: T.n2 };
const helpText: React.CSSProperties = { fontSize: T.text.label.fontSize, color: T.soft, lineHeight: 1.5, margin: 0 };
const errText: React.CSSProperties = { fontSize: T.text.label.fontSize, color: T.red1, lineHeight: 1.5, margin: 0, display: "flex", alignItems: "center", gap: T.space[2] };

const optOf = (o: FieldOption | string): FieldOption => (typeof o === "string" ? { value: o } : o);

export interface FieldSelectProps {
  label: string;
  value: string;
  options: (FieldOption | string)[];
  onChange?: (value: string) => void;
  help?: string;
  readOnly?: boolean;
  required?: boolean;
}

export function FieldSelect({ label, value, options, onChange, help, readOnly, required }: FieldSelectProps) {
  const t = useT();
  const dsOptions = useMemo<DropdownOption[]>(
    () => options.map(optOf).map((o) => ({ value: o.value, label: o.label ? (o.labelKey ? t(o.labelKey, o.label) : o.label) : o.value, supportingText: o.hint })),
    [options, t],
  );
  // An unknown stored value still has to be shown, never silently swapped.
  const known = dsOptions.some((o) => o.value === value);
  const all = known || !value ? dsOptions : [{ value, label: value }, ...dsOptions];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: T.space[1] }}>
      <div style={fieldLabel}>{label}{required ? <span style={{ color: T.red1 }} aria-hidden="true"> *</span> : null}</div>
      <Dropdown
        options={all}
        value={value || null}
        onChange={(v) => onChange?.(v)}
        disabled={readOnly}
        aria-label={label}
        /* The settings pane scrolls — the menu must live in a portal or it gets clipped. */
        portal
      />
      {help ? <p style={helpText}>{help}</p> : null}
    </div>
  );
}

/* ── URL field ───────────────────────────────────────────────────────────────
 * Validation is a regex FIRST (cheap, explicit about what we accept: http/https,
 * a dotted host or localhost, optional port/path/query/fragment, no spaces) and
 * then a URL parse as the second gate, because a regex alone accepts things the
 * platform rejects. Errors appear on blur or on submit — never while the user is
 * still typing the third character. */
export const URL_RE = /^https?:\/\/(?:localhost(?::\d{1,5})?|(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,63}(?::\d{1,5})?)(?:\/[^\s?#]*)?(?:\?[^\s#]*)?(?:#\S*)?$/i;

export function isValidUrl(raw: string): boolean {
  const v = raw.trim();
  if (!URL_RE.test(v)) return false;
  try { const u = new URL(v); return u.protocol === "http:" || u.protocol === "https:"; } catch { return false; }
}

/** Adds the scheme a user usually omits, so "acme.com/events" validates. */
export function normalizeUrl(raw: string): string {
  const v = raw.trim();
  if (!v) return v;
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(v) ? v : `https://${v}`;
}

export interface UrlFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Reported on every change so the screen can block Save. */
  onValidityChange?: (valid: boolean) => void;
  help?: string;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  /** Show the error even if the field was never blurred (e.g. after a Save attempt). */
  forceShowError?: boolean;
}

export function UrlField({ label, value, onChange, onValidityChange, help, placeholder, readOnly, required, forceShowError }: UrlFieldProps) {
  const t = useT();
  const uid = useId().replace(/:/g, "");
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);
  const empty = value.trim().length === 0;
  const valid = empty ? !required : isValidUrl(value);
  const show = (touched || !!forceShowError) && !valid;
  const insecure = valid && /^http:\/\//i.test(value.trim());

  useEffect(() => { onValidityChange?.(valid); }, [valid, onValidityChange]);

  const msg = empty
    ? t("settings.fields.urlRequired", "Enter the address your booking pages are published on.")
    : t("settings.fields.urlInvalid", "That isn't a valid web address — it should look like https://events.acme.com.");
  const describedBy = [help ? `${uid}-help` : null, show ? `${uid}-err` : null, insecure ? `${uid}-warn` : null].filter(Boolean).join(" ") || undefined;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: T.space[1] }}>
      <label htmlFor={`${uid}-input`} style={fieldLabel}>
        {label}{required ? <span style={{ color: T.red1 }} aria-hidden="true"> *</span> : null}
      </label>
      <div style={{
        display: "flex", alignItems: "center", height: 44, overflow: "hidden", boxSizing: "border-box",
        // Focus treatment aligned to the DS Input — re-aligned 2026-08-10. It was
        // copied from the 3px-ring era and never followed Input down to 1px, so this
        // field rendered a ring 3x heavier than every DS control beside it. It also
        // kept a BRAND ring while showing a red border in the error state; the DS
        // makes error+focus ONE signal, so the ring turns red too.
        border: `1px solid ${show ? T.red1 : focused ? T.p1 : T.n4}`, borderRadius: T.radius.control, background: readOnly ? T.n6 : T.white,
        boxShadow: focused && !readOnly ? `0 0 0 1px ${show ? T.red1 : T.focus}` : "none",
        transition: "border-color 120ms, box-shadow 120ms",
      }}>
        <span style={{ padding: `0 ${T.space[3]} 0 ${T.space[4]}`, color: show ? T.red1 : T.soft, flexShrink: 0, display: "inline-flex" }}><Icon.globe size={14} /></span>
        {/* ui-raw: jrni-ui Input can't reproduce this control — fixed 44px row height (Input only offers 32/40 via size sm/md, and `style` doesn't reach the inner bordered row), plus up to three simultaneous helper lines (help + error + insecure-http warning) where Input/Field render at most one. Swapping would regress row height or drop a message. */}
        <input
          id={`${uid}-input`}
          type="url"
          inputMode="url"
          autoComplete="url"
          spellCheck={false}
          value={value}
          placeholder={placeholder}
          readOnly={readOnly}
          aria-invalid={show || undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); setTouched(true); const n = normalizeUrl(value); if (n !== value) onChange(n); }}
          style={{ flex: 1, minWidth: 0, border: "none", outline: "none", height: "100%", padding: `0 ${T.space[3]} 0 0`, fontSize: T.text.body.fontSize, fontFamily: T.font, color: T.n1, background: "transparent" }}
        />
      </div>
      {help && !show ? <p id={`${uid}-help`} style={helpText}>{help}</p> : null}
      {show ? <p id={`${uid}-err`} style={errText}><Icon.warn size={12} /> {msg}</p> : null}
      {insecure ? (
        <p id={`${uid}-warn`} style={{ ...helpText, color: T.orange1 }}>
          {t("settings.fields.urlInsecure", "This is an http:// address — guests' browsers will flag it as not secure. Use https:// if you can.")}
        </p>
      ) : null}
    </div>
  );
}

/* ── Regional formatting helpers ─────────────────────────────────────────────
 * Pure functions so the settings screen can render a LIVE PREVIEW of every
 * format choice (the reference design leads each card with one) and the rest of
 * the app can reuse the same rules later. */
export const DATE_FORMAT_OPTIONS: FieldOption[] = [
  { value: "DD.MM.YYYY", hint: "31.12.2026" },
  { value: "DD/MM/YYYY", hint: "31/12/2026" },
  { value: "MM/DD/YYYY", hint: "12/31/2026" },
  { value: "YYYY-MM-DD", hint: "2026-12-31" },
  { value: "D MMMM YYYY", hint: "31 December 2026" },
];
export function formatDateSample(pattern: string, d = new Date(2026, 11, 31)): string {
  const dd = String(d.getDate()).padStart(2, "0"), mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  const month = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][d.getMonth()];
  return pattern
    .replace("MMMM", month).replace("YYYY", yyyy)
    .replace("DD", dd).replace("MM", mm)
    .replace(/\bD\b/, String(d.getDate()));
}
export const formatTimeSample = (fmt: string) => (fmt === "12h" ? "2:30 PM" : "14:30");

export const CURRENCY_SYMBOLS: Record<string, string> = { USD: "$", EUR: "€", GBP: "£", CHF: "Fr.", AUD: "A$", CAD: "C$", JPY: "¥" };
export function formatMoneySample(currency: string, position: string, decimal: string, thousands: string): string {
  const symbol = CURRENCY_SYMBOLS[currency] ?? currency;
  const groups = ["1", "234", "567"].join(thousands === "none" ? "" : thousands);
  const cents = currency === "JPY" ? "" : `${decimal}89`;
  const amount = `${groups}${cents}`;
  return position === "amount-symbol" ? `${amount} ${symbol}` : `${symbol}${symbol.length > 1 ? " " : ""}${amount}`;
}

export const ADDRESS_PLACEHOLDERS: { token: string; label: string; labelKey: string }[] = [
  { token: "{{street}}", label: "Street", labelKey: "settings.addressToken.street.label" },
  { token: "{{house_number}}", label: "House number", labelKey: "settings.addressToken.house_number.label" },
  { token: "{{zip}}", label: "ZIP/Postal code", labelKey: "settings.addressToken.zip.label" },
  { token: "{{city}}", label: "City", labelKey: "settings.addressToken.city.label" },
  { token: "{{state}}", label: "State/Province", labelKey: "settings.addressToken.state.label" },
  { token: "{{country}}", label: "Country", labelKey: "settings.addressToken.country.label" },
];
export const ADDRESS_PRESETS: Record<string, string> = {
  Switzerland: "{{street}} {{house_number}}\n{{zip}} {{city}}\n{{country}}",
  Germany: "{{street}} {{house_number}}\n{{zip}} {{city}}\n{{country}}",
  "United States": "{{house_number}} {{street}}\n{{city}}, {{state}} {{zip}}\n{{country}}",
  "United Kingdom": "{{house_number}} {{street}}\n{{city}}\n{{zip}}\n{{country}}",
  France: "{{house_number}} {{street}}\n{{zip}} {{city}}\n{{country}}",
  Japan: "{{zip}}\n{{state}} {{city}}\n{{street}} {{house_number}}\n{{country}}",
};
const ADDRESS_SAMPLE: Record<string, string> = {
  street: "Bahnhofstrasse", house_number: "42", zip: "8001", city: "Zürich", state: "ZH", country: "Switzerland",
};
export function formatAddressSample(template: string): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, k: string) => ADDRESS_SAMPLE[k] ?? `{{${k}}}`)
    .split("\n").map((l) => l.trim()).filter(Boolean).join("\n");
}
